import React from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import $routes from 'js/core_pre_webpack/routes';
import fetchWithCsrf from 'js/utils/fetch_with_csrf';
import styled from 'astroturf/react';

import { ReaderProps } from '../../Reader/Reader';
import Reader from './Reader';
import RelatedPapersSidebar from './RelatedPapersSidebar';
import { Paper } from '../../shared/types';
import { SidebarComments } from '../Sidebar/SidebarComments';
import { NewsType } from 'js/social/shared/types';
import { ImpressionPropsGenerator } from 'js/social/ImpressionHelper';

export const Sidebar = styled.div`
  flex-basis: 400px;
  flex-shrink: 0;
  display: flex;
  flex-direction: column;

  @import 'stylesheets/design_system/_breakpoint.scss';

  @include breakpoint(small, medium) {
    display: none;
  }

  padding-right: 24px;
  overflow-y: auto;
  overflow-x: hidden;

  background:
    /* Shadow covers */
    linear-gradient(#f4f7fc 30%, rgba(255, 255, 255, 0)),
    /* Shadows */ linear-gradient(rgba(0, 0, 0, 0.2), rgba(0, 0, 0, 0));
  background-repeat: no-repeat;
  background-color: #f4f7fc;
  background-size:
    100% 40px,
    100% 10px;

  /* Opera doesn't support this in the shorthand */
  background-attachment: local, scroll;
`;

type Props = Omit<ReaderProps, 'work' | 'impressionProps'> & {
  work: Paper;
  showRelatedPaperSidebar: boolean;
  newsType: NewsType;
  newsId: string | number;
  impressionPropsGen: ImpressionPropsGenerator;
};

async function fetchRelatedPapers(
  workId: number,
  signal?: AbortSignal,
): Promise<{ relatedWorks: Paper[] }> {
  const resp = await fetchWithCsrf(
    $routes.api_v0_community_related_works_for_work_path({
      subdomain_param: 'api',
      work_id: workId,
    }),
    {
      signal,
    },
  );
  if (!resp.ok) {
    throw new Error('Response failed');
  }
  return await resp.json();
}

function useRelatedWorks(
  workId: number,
  enabled: boolean,
): {
  isLoading: boolean;
  isError: boolean;
  relatedWorks: Paper[] | undefined;
  updateRelatedWorkBookmarkStatus: (
    workId: number,
    bookmarked: boolean,
  ) => void;
} {
  const { isFetching, isError, data } = useQuery({
    queryKey: ['relatedWorks', workId],
    queryFn: ({ signal }) => fetchRelatedPapers(workId, signal),
    enabled: enabled,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const queryClient = useQueryClient();

  function updateRelatedWorkBookmarkStatus(
    relatedWorkId: number,
    bookmarked: boolean,
  ) {
    queryClient.setQueryData(
      ['relatedWorks', workId],
      (oldData?: { relatedWorks: Paper[] }) => {
        if (!oldData) {
          return oldData;
        }

        const updatedRelatedWorks: Paper[] = oldData.relatedWorks.map(
          (paper: Paper) => ({
            ...paper,
            bookmarkedByCurrentUser:
              paper.id === relatedWorkId
                ? bookmarked
                : paper.bookmarkedByCurrentUser,
          }),
        );

        return {
          ...oldData,
          relatedWorks: updatedRelatedWorks,
        };
      },
    );
  }

  return {
    isLoading: isFetching,
    isError,
    relatedWorks: data?.relatedWorks,
    updateRelatedWorkBookmarkStatus,
  };
}

export function ReaderWithLoadedSidebar({
  newsType,
  newsId,
  showRelatedPaperSidebar,
  work,
  impressionPropsGen,
  ...restProps
}: Props) {
  const { isLoading, isError, relatedWorks, updateRelatedWorkBookmarkStatus } =
    useRelatedWorks(work.id, showRelatedPaperSidebar);

  const shouldRenderRelatedPapersSidebar =
    showRelatedPaperSidebar &&
    !isError &&
    (isLoading || relatedWorks?.length !== 0);

  return (
    <Reader
      fullWidth={showRelatedPaperSidebar}
      fullWidthCoreX={showRelatedPaperSidebar}
      sidebar={
        <Sidebar>
          <SidebarComments
            newsType={newsType}
            newsId={newsId}
            page="home"
            impressionPropsGen={impressionPropsGen}
          />
          {shouldRenderRelatedPapersSidebar && (
            <RelatedPapersSidebar
              relatedPapers={isLoading ? undefined : relatedWorks}
              updateWorkBookmarkStatus={updateRelatedWorkBookmarkStatus}
              sourceWorkId={work.id}
            />
          )}
        </Sidebar>
      }
      work={work}
      impressionProps={impressionPropsGen()()()}
      {...restProps}
    />
  );
}
